export const createAccessControlService = ({
  supabase,
  accessRequestsTable,
  isMissingRelationError,
}) => {
  const allowAccessControlBypass = (
    process.env.NODE_ENV !== "production" &&
    String(process.env.REACT_APP_ALLOW_ACCESS_CONTROL_BYPASS || "").trim().toLowerCase() === "true"
  );

  const getBypassResult = () => (
    allowAccessControlBypass
      ? { status: "approved", record: null, accessControlAvailable: false, isAdmin: false }
      : { status: "pending", record: null, accessControlAvailable: true, isAdmin: false, error: new Error("Access control is not configured.") }
  );

  const checkIsAccessAdmin = async (userId) => {
    if (!supabase || !userId) {
      return { ok: true, isAdmin: false };
    }

    const { data, error } = await supabase
      .from("access_admins")
      .select("user_id")
      .eq("user_id", userId)
      .maybeSingle();

    if (error) {
      if (isMissingRelationError(error)) {
        return { ok: false, missingRelation: true, error };
      }
      throw error;
    }

    return { ok: true, isAdmin: !!data?.user_id };
  };

  const sendAccessApprovedMail = async (entry) => {
    if (!supabase) {
      throw new Error("Supabase ist nicht verfügbar.");
    }

    const approvedUserId = String(entry?.user_id || "").trim();
    if (!approvedUserId) {
      throw new Error("Benutzer-ID fehlt für Freigabe-E-Mail.");
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = String(sessionData?.session?.access_token || "").trim();
    if (!accessToken) {
      throw new Error("Nicht authentifiziert.");
    }

    const response = await fetch("/api/access-approved-notify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        approvedUserId,
      }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.error || "E-Mail Versand fehlgeschlagen.");
    }

    return true;
  };

  const syncOwnAccessRequest = async ({ currentSession, isAccessControlAvailable }) => {
    if (!currentSession?.user || !supabase) {
      return { status: "idle", record: null, accessControlAvailable: isAccessControlAvailable, isAdmin: false };
    }

    if (!isAccessControlAvailable) {
      return allowAccessControlBypass
        ? { status: "approved", record: null, accessControlAvailable: false, isAdmin: false }
        : { status: "pending", record: null, accessControlAvailable: true, isAdmin: false };
    }

    const email = String(currentSession.user.email || "").trim().toLowerCase();
    const fullName = String(
      currentSession.user.user_metadata?.full_name
      || currentSession.user.user_metadata?.display_name
      || ""
    ).trim();

    try {
      const adminCheck = await checkIsAccessAdmin(currentSession.user.id);
      if (!adminCheck.ok && adminCheck.missingRelation) {
        return getBypassResult();
      }
      const isAdmin = !!adminCheck.isAdmin;

      const { data, error } = await supabase
        .from(accessRequestsTable)
        .select("*")
        .eq("user_id", currentSession.user.id)
        .maybeSingle();

      if (error) {
        if (isMissingRelationError(error)) {
          return getBypassResult();
        }
        throw error;
      }

      let nextRecord = data;
      const shouldApproveAdmin = isAdmin && (!nextRecord || nextRecord.status !== "approved");
      const shouldCreate = !nextRecord;
      const shouldRefreshProfile = isAdmin && !!nextRecord && (
        nextRecord.email !== email ||
        (fullName && nextRecord.full_name !== fullName)
      );

      if (shouldCreate || shouldApproveAdmin || shouldRefreshProfile) {
        const payload = {
          user_id: currentSession.user.id,
          email,
          full_name: fullName || null,
          status: isAdmin ? "approved" : (nextRecord?.status || "pending"),
          approved_by: isAdmin ? currentSession.user.id : (nextRecord?.approved_by || null),
          approved_at: isAdmin ? new Date().toISOString() : (nextRecord?.approved_at || null),
        };

        const { data: upserted, error: upsertError } = await supabase
          .from(accessRequestsTable)
          .upsert(payload, { onConflict: "user_id" })
          .select("*")
          .single();

        if (upsertError) {
          if (isMissingRelationError(upsertError)) {
            return getBypassResult();
          }
          throw upsertError;
        }

        nextRecord = upserted;
      }

      const resolvedStatus = isAdmin ? "approved" : (nextRecord?.status || "pending");
      return { status: resolvedStatus, record: nextRecord, accessControlAvailable: true, isAdmin };
    } catch (error) {
      return { status: "pending", record: null, accessControlAvailable: isAccessControlAvailable, isAdmin: false, error };
    }
  };

  const loadAccessRequests = async ({ isAdmin, isAccessControlAvailable }) => {
    if (!supabase || !isAdmin || !isAccessControlAvailable) {
      return { ok: true, requests: [] };
    }

    try {
      const { data, error } = await supabase
        .from(accessRequestsTable)
        .select("*")
        .order("status", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) {
        if (isMissingRelationError(error)) {
          return { ok: true, requests: [], accessControlAvailable: false };
        }
        throw error;
      }

      return { ok: true, requests: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { ok: false, requests: [], error };
    }
  };

  const updateAccessRequestStatus = async ({ entry, nextStatus, sessionUserId }) => {
    if (!supabase || !entry?.user_id) return { ok: false, error: new Error("Ungültiger Eintrag.") };

    try {
      const { error } = await supabase
        .from(accessRequestsTable)
        .update({
          status: nextStatus,
          approved_by: sessionUserId,
          approved_at: new Date().toISOString(),
        })
        .eq("user_id", entry.user_id);

      if (error) throw error;
      return { ok: true };
    } catch (error) {
      return { ok: false, error };
    }
  };

  return {
    sendAccessApprovedMail,
    syncOwnAccessRequest,
    loadAccessRequests,
    updateAccessRequestStatus,
  };
};
